import { useEffect, useRef, useState } from "react";
import { CarFront, Check, ChevronDown, ChevronRight, Dumbbell, Heart, HelpCircle, Languages, Laptop, LogOut, MapPin, Menu, Package, Search, Shirt, ShoppingBasket, ShoppingCart, Smartphone, Sofa, Sparkles, User, UserRound, WalletCards, X } from "lucide-react";
import "./LandingNavbar.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import { useAppDispatch } from "../../../Redux/hooks";
import { logout } from "../../../Redux/custmoer_auth/slice";
type Category = { name: string; icon: React.ReactNode; };
type Language = { name: string; shortName: string; };
type Currency = { code: string; symbol: string; name: string; };
const categories: Category[] = [
    { name: "Grocery & Fresh Produce", icon: <ShoppingBasket size={18} strokeWidth={1.8} /> },
    { name: "Fashion & Clothing", icon: <Shirt size={18} strokeWidth={1.8} /> },
    { name: "Electronics", icon: <Laptop size={18} strokeWidth={1.8} /> },
    { name: "Home & Furniture", icon: <Sofa size={18} strokeWidth={1.8} /> },
    { name: "Beauty & Personal Care", icon: <Sparkles size={18} strokeWidth={1.8} /> },
    { name: "Mobiles & Accessories", icon: <Smartphone size={18} strokeWidth={1.8} /> },
    { name: "Sports & Fitness", icon: <Dumbbell size={18} strokeWidth={1.8} /> },
    { name: "Automotive", icon: <CarFront size={18} strokeWidth={1.8} /> },
];
const languages: Language[] = [
    { name: "English", shortName: "EN" },
    { name: "Hindi", shortName: "HI" },
    { name: "Telugu", shortName: "TE" },
    { name: "Tamil", shortName: "TA" },
    { name: "Kannada", shortName: "KN" },
    { name: "Malayalam", shortName: "ML" },
];
const currencies: Currency[] = [
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
];
const LandingNavbar = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isAuthenticated, user } = useAuth();
    const { cartCount, cartTotal } = useCart();
    const { wishlistCount } = useWishlist();
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [localeOpen, setLocaleOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const [currencyOpen, setCurrencyOpen] = useState(false);
    const [language, setLanguage] = useState("English");
    const [currency, setCurrency] = useState("INR");
    const categoryRef = useRef<HTMLDivElement>(null);
    const localeRef = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);
    const selectedLanguage = languages.find((item) => item.name === language) ?? languages[0];
    const selectedCurrency = currencies.find((item) => item.code === currency) ?? currencies[0];
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (categoryRef.current && !categoryRef.current.contains(target)) setCategoryOpen(false);
            if (localeRef.current && !localeRef.current.contains(target)) { setLocaleOpen(false); setLanguageOpen(false); setCurrencyOpen(false); }
            if (profileRef.current && !profileRef.current.contains(target)) setProfileOpen(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);
    const handleCategoryOpen = () => {
        setCategoryOpen((prev) => !prev);
        setLocaleOpen(false);
        setProfileOpen(false);
        setLanguageOpen(false);
        setCurrencyOpen(false);
    };
    const handleLocaleOpen = () => {
        setLocaleOpen((prev) => !prev);
        setCategoryOpen(false);
        setProfileOpen(false);
        setLanguageOpen(false);
        setCurrencyOpen(false);
    };
    const handleProfileOpen = () => {
        setProfileOpen((prev) => !prev);
        setCategoryOpen(false);
        setLocaleOpen(false);
        setLanguageOpen(false);
        setCurrencyOpen(false);
    };

    const handleAuthNavigate = (path: "/SignIn" | "/SignUp") => {
        setProfileOpen(false);
        setMobileMenuOpen(false);
        navigate(path);
    };
    const handleMenuNavigate = (path: string) => {
        setProfileOpen(false);
        setMobileMenuOpen(false);
        navigate(path);
    };
    const handleLogout = () => {
        setProfileOpen(false);
        setMobileMenuOpen(false);
        dispatch(logout());
        navigate("/");
    };
    const userInitial = (user?.fullName || user?.name || user?.email || "")?.trim()?.charAt(0)?.toUpperCase();
    const userName = user?.fullName || user?.name || (user?.email ? user.email.split("@")[0] : "My Account");
    const userSubtitle = user?.email || user?.phone || "Manage your account and shopping.";
    const formatCartPrice = (value: number) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    return (
        <>
            <header className="landing-navbar">
                <div className="landing-navbar-container">
                    <button type="button" className="landing-navbar-logo" aria-label="Go to Salar home" onClick={() => navigate("/")}>
                        <span className="landing-logo-aura" />
                        <img src="../../assets/images/logos/salarlogo.png" alt="Salar" />
                    </button>
                    <div className="landing-navbar-left">
                        <button type="button" className="landing-deals-btn" onClick={() => navigate("/todays-deals")}>
                            <span className="landing-deals-icon">
                                <Sparkles size={16} strokeWidth={1.9} />
                            </span>
                            <span>Today's Deals</span>
                        </button>
                        <div className="landing-dropdown-wrapper" ref={categoryRef}>
                            <button type="button" className={`landing-category-trigger ${categoryOpen ? "active" : ""}`} onClick={handleCategoryOpen}>
                                <span>Categories</span>
                                <ChevronDown size={15} className={categoryOpen ? "rotate" : ""} />
                            </button>
                            {categoryOpen && (
                                <div className="landing-category-dropdown">
                                    <div className="landing-dropdown-header">
                                        <div>
                                            <span>DISCOVER</span>
                                            <h4>Shop by Category</h4>
                                        </div>
                                        <button type="button">View All</button>
                                    </div>
                                    <div className="landing-category-list">
                                        {categories.map((category) => (
                                            <button type="button" className="landing-category-item" key={category.name}>
                                                <span className="landing-category-icon">{category.icon}</span>
                                                <span className="landing-category-name">{category.name}</span>
                                                <ChevronRight size={15} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <form className="landing-navbar-search" onSubmit={(event) => event.preventDefault()}>
                        <Search size={19} strokeWidth={1.8} />
                        <input type="text" placeholder="Search products, brands and categories" aria-label="Search Salar" />
                    </form>
                    <div className="landing-navbar-right">
                        <div className="landing-locale-wrapper" ref={localeRef}>
                            <button type="button" className={`landing-locale-trigger ${localeOpen ? "active" : ""}`} onClick={handleLocaleOpen}>
                                <span className="landing-locale-icon">
                                    <Languages size={18} strokeWidth={1.8} />
                                </span>
                                <span className="landing-locale-copy">
                                    <small>Language</small>
                                    <strong>{selectedLanguage.shortName} · {currency}</strong>
                                </span>
                                <ChevronDown size={14} className={localeOpen ? "rotate" : ""} />
                            </button>
                            {localeOpen && (
                                <div className="landing-locale-dropdown">
                                    <div className="landing-locale-header">
                                        <span className="landing-locale-header-icon">
                                            <Languages size={20} />
                                        </span>
                                        <div>
                                            <h4>Shopping Preferences</h4>
                                            <p>Personalize language and currency.</p>
                                        </div>
                                    </div>
                                    <div className="landing-delivery-card">
                                        <span className="landing-delivery-icon">
                                            <MapPin size={18} />
                                        </span>
                                        <div>
                                            <small>Delivering to</small>
                                            <strong>India</strong>
                                        </div>
                                    </div>
                                    <div className="landing-preference-group">
                                        <span className="landing-preference-label">Language</span>
                                        <div className="landing-preference-dropdown">
                                            <button type="button" className={`landing-preference-trigger ${languageOpen ? "active" : ""}`} onClick={() => {
                                                setLanguageOpen((prev) => !prev);
                                                setCurrencyOpen(false);
                                            }}>
                                                <span className="landing-preference-left">
                                                    <span className="landing-preference-code">{selectedLanguage.shortName}</span>
                                                    <span className="landing-preference-copy">
                                                        <small>Display language</small>
                                                        <strong>{selectedLanguage.name}</strong>
                                                    </span>
                                                </span>
                                                <ChevronDown size={16} className={languageOpen ? "rotate" : ""} />
                                            </button>
                                            {languageOpen && (
                                                <div className="landing-preference-options">
                                                    {languages.map((item) => (
                                                        <button type="button" key={item.name} className={language === item.name ? "selected" : ""} onClick={() => { setLanguage(item.name); setLanguageOpen(false); }}>
                                                            <span className="landing-option-left">
                                                                <span className="landing-option-code">{item.shortName}</span>
                                                                <span>{item.name}</span>
                                                            </span>
                                                            {language === item.name && <Check size={16} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="landing-preference-group">
                                        <span className="landing-preference-label">Currency</span>
                                        <div className="landing-preference-dropdown">
                                            <button type="button" className={`landing-preference-trigger ${currencyOpen ? "active" : ""}`} onClick={() => { setCurrencyOpen((prev) => !prev); setLanguageOpen(false); }}>
                                                <span className="landing-preference-left">
                                                    <span className="landing-preference-code landing-currency-code">{selectedCurrency.symbol}</span>
                                                    <span className="landing-preference-copy">
                                                        <small>Shopping currency</small>
                                                        <strong>{selectedCurrency.code}</strong>
                                                    </span>
                                                </span>
                                                <ChevronDown size={16} className={currencyOpen ? "rotate" : ""} />
                                            </button>
                                            {currencyOpen && (
                                                <div className="landing-preference-options">
                                                    {currencies.map((item) => (
                                                        <button type="button" key={item.code} className={currency === item.code ? "selected" : ""} onClick={() => { setCurrency(item.code); setCurrencyOpen(false); }}>
                                                            <span className="landing-option-left">
                                                                <span className="landing-option-code landing-option-currency">{item.symbol}</span>
                                                                <span className="landing-currency-info">
                                                                    <strong>{item.code}</strong>
                                                                    <small>{item.name}</small>
                                                                </span>
                                                            </span>
                                                            {currency === item.code && <Check size={16} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button type="button" className="landing-apply-btn" onClick={() => { setLocaleOpen(false); setLanguageOpen(false); setCurrencyOpen(false); }}>
                                        Apply Preferences
                                    </button>
                                </div>
                            )}
                        </div>
                        <span className="landing-action-divider" />
                        {isAuthenticated && (
                            <>
                                <button type="button" className="landing-action-btn landing-wishlist-btn" aria-label="Wishlist" onClick={() => navigate("/wishlist")}>
                                    <span className="landing-action-icon">
                                        <Heart size={21} strokeWidth={1.8} />
                                        <span className="landing-action-count">{wishlistCount}</span>
                                    </span>
                                    <span className="landing-action-copy">
                                        <small>Saved Items</small>
                                        <strong>Wishlist</strong>
                                    </span>
                                </button>
                                <button type="button" className="landing-action-btn landing-cart" aria-label="Cart" onClick={() => navigate("/Cart")}>
                                    <span className="landing-action-icon">
                                        <ShoppingCart size={22} strokeWidth={1.8} />
                                        <span className="landing-action-count">{cartCount}</span>
                                    </span>
                                    <span className="landing-action-copy">
                                        <small>Shopping Cart</small>
                                        <strong>{selectedCurrency.symbol}{formatCartPrice(cartTotal)}</strong>
                                    </span>
                                </button>
                            </>
                        )}
                        <div className="landing-profile-wrapper" ref={profileRef}>
                            <button type="button" className={`landing-profile-trigger ${profileOpen ? "active" : ""}`} onClick={handleProfileOpen}>
                                <Menu size={20} strokeWidth={1.8} />
                                <span className="landing-profile-avatar">
                                    {isAuthenticated && userInitial ? (
                                        <b style={{ fontSize: "14px", fontWeight: 700, lineHeight: 1 }}>{userInitial}</b>
                                    ) : (
                                        <User size={18} strokeWidth={1.8} />
                                    )}
                                </span>
                            </button>
                            {profileOpen && (
                                <div className="landing-profile-dropdown">
                                    <div className="landing-profile-header">
                                        <span className="landing-profile-avatar-large">
                                            {isAuthenticated && userInitial ? (
                                                <b style={{ fontSize: "16px", fontWeight: 700, lineHeight: 1 }}>{userInitial}</b>
                                            ) : (
                                                <User size={21} />
                                            )}
                                        </span>
                                        <div>
                                            <h4>{isAuthenticated ? userName : "Welcome to Salar"}</h4>
                                            <p>{isAuthenticated ? userSubtitle : "Sign in to manage your shopping account."}</p>
                                        </div>
                                    </div>
                                    {!isAuthenticated && (
                                        <div className="landing-auth-actions">
                                            <button
                                                type="button"
                                                className="landing-signin-btn"
                                                onClick={() => handleAuthNavigate("/SignIn")}
                                            >
                                                Sign In
                                            </button>
                                            <button
                                                type="button"
                                                className="landing-create-btn"
                                                onClick={() => handleAuthNavigate("/SignUp")}
                                            >
                                                Create Account
                                            </button>
                                        </div>
                                    )}
                                    {isAuthenticated && (
                                        <>
                                            <div className="landing-profile-divider" />
                                            <div className="landing-profile-menu">
                                                <button type="button" onClick={() => handleMenuNavigate("/Dashboard")}>
                                                    <span className="landing-profile-menu-icon"><UserRound size={18} /></span>
                                                    <span className="landing-profile-menu-copy">
                                                        <strong>My Account</strong>
                                                        <small>Profile and account settings</small>
                                                    </span>
                                                    <ChevronRight size={15} />
                                                </button>
                                                <button type="button" onClick={() => handleMenuNavigate("/Orders")}>
                                                    <span className="landing-profile-menu-icon"><Package size={18} /></span>
                                                    <span className="landing-profile-menu-copy">
                                                        <strong>My Orders</strong>
                                                        <small>Track and manage purchases</small>
                                                    </span>
                                                    <ChevronRight size={15} />
                                                </button>
                                                <button type="button" onClick={() => handleMenuNavigate("/wishlist")}>
                                                    <span className="landing-profile-menu-icon"><Heart size={18} /></span>
                                                    <span className="landing-profile-menu-copy">
                                                        <strong>Wishlist</strong>
                                                        <small>View your saved products</small>
                                                    </span>
                                                    <ChevronRight size={15} />
                                                </button>
                                                <button type="button" onClick={() => handleMenuNavigate("/SalarPay")}>
                                                    <span className="landing-profile-menu-icon"><WalletCards size={18} /></span>
                                                    <span className="landing-profile-menu-copy">
                                                        <strong>Payments & Wallet</strong>
                                                        <small>Manage payment preferences</small>
                                                    </span>
                                                    <ChevronRight size={15} />
                                                </button>
                                                <button type="button" onClick={() => handleMenuNavigate("/Tickets")}>
                                                    <span className="landing-profile-menu-icon"><HelpCircle size={18} /></span>
                                                    <span className="landing-profile-menu-copy">
                                                        <strong>Help & Support</strong>
                                                        <small>Get assistance with orders</small>
                                                    </span>
                                                    <ChevronRight size={15} />
                                                </button>
                                                <button type="button" onClick={handleLogout}>
                                                    <span className="landing-profile-menu-icon"><LogOut size={18} /></span>
                                                    <span className="landing-profile-menu-copy">
                                                        <strong>Logout</strong>
                                                        <small>Sign out of your account</small>
                                                    </span>
                                                    <ChevronRight size={15} />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <button type="button" className="landing-mobile-menu-btn" onClick={() => setMobileMenuOpen((prev) => !prev)} aria-label="Toggle menu">
                            {mobileMenuOpen ? <X size={21} /> : <Menu size={21} />}
                        </button>
                    </div>
                </div>
            </header>
            {mobileMenuOpen && (
                <div className="landing-mobile-menu">
                    <form className="landing-mobile-search" onSubmit={(event) => event.preventDefault()}>
                        <Search size={18} />
                        <input type="text" placeholder="Search products, brands and categories" />
                    </form>
                    <button type="button" className="landing-mobile-deal" onClick={() => handleMenuNavigate("/todays-deals")}>
                        <Sparkles size={18} />
                        <span>Today's Deals</span>
                    </button>
                    <button type="button" className="landing-mobile-nav-item" onClick={() => setCategoryOpen((prev) => !prev)}>
                        <span>Categories</span>
                        <ChevronDown size={16} className={categoryOpen ? "rotate" : ""} />
                    </button>
                    {categoryOpen && (
                        <div className="landing-mobile-categories">
                            {categories.map((category) => (
                                <button type="button" key={category.name}>
                                    <span>{category.icon}</span>
                                    <strong>{category.name}</strong>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="landing-mobile-divider" />
                    <button type="button" className="landing-mobile-row">
                        <span> <Languages size={18} /> Language </span>
                        <strong>{selectedLanguage.shortName} · {currency}</strong>
                    </button>
                    <div className="landing-mobile-location">
                        <MapPin size={18} />
                        <div>
                            <small>Deliver to</small>
                            <strong>India</strong>
                        </div>
                    </div>
                    {isAuthenticated && (
                        <>
                            <button type="button" className="landing-mobile-row" onClick={() => handleMenuNavigate("/wishlist")}>
                                <span> <Heart size={18} /> Wishlist</span>
                                <strong>{wishlistCount}</strong>
                            </button>
                            <button type="button" className="landing-mobile-row" onClick={() => handleMenuNavigate("/Cart")}>
                                <span> <ShoppingCart size={18} /> My Cart</span>
                                <strong>{selectedCurrency.symbol}{formatCartPrice(cartTotal)}</strong>
                            </button>
                        </>
                    )}
                    <div className="landing-mobile-divider" />
                    {isAuthenticated ? (
                        <>
                            <button type="button" className="landing-mobile-row" onClick={() => handleMenuNavigate("/Dashboard")}>
                                <span> <UserRound size={18} /> {userName}</span>
                                <ChevronRight size={16} />
                            </button>
                            <button
                                type="button"
                                className="landing-mobile-signin"
                                onClick={handleLogout}
                            >
                                Logout
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="landing-mobile-signin"
                                onClick={() => handleAuthNavigate("/SignIn")}
                            >
                                Sign In
                            </button>
                            <button
                                type="button"
                                className="landing-mobile-create"
                                onClick={() => handleAuthNavigate("/SignUp")}
                            >
                                Create Account
                            </button>
                        </>
                    )}
                </div>
            )}
        </>
    );
};
export default LandingNavbar;